export type ProductCategory = {
  id: number;
  name: string;
  category_type: unknown;
};

function extractCategoryList(payload: unknown): unknown[] {
  if (Array.isArray(payload)) return payload;
  if (!payload || typeof payload !== "object") return [];

  const body = payload as Record<string, unknown>;
  if (Array.isArray(body.items)) return body.items;
  if (Array.isArray(body.data)) return body.data;
  return [];
}

type CategoryRow = ProductCategory & { key: string };

function toCategoryRow(row: unknown): CategoryRow | null {
  if (!row || typeof row !== "object") return null;

  const category = row as Partial<ProductCategory>;
  const id = Number(category.id);
  const name = String(category.name ?? "").trim();

  if (!Number.isInteger(id) || id <= 0 || !name) return null;

  return {
    id,
    name,
    category_type: category.category_type,
    key: name.toLocaleLowerCase(),
  };
}

/**
 * Every category is listed, whatever its `category_type`, until the client
 * confirms which types belong in the product filter: the ERP marks some rows
 * `other`, some `service` and many `false`, and the untyped ones are not
 * necessarily empty (`Remover` 16 holds the items that `Remover` 33 lacks).
 *
 * Repeated names collapse onto the last record in the response, since that is
 * the ID `/products?categ_id=…` actually returns items for.
 */
export function normalizeProductCategories(
  payload: unknown
): ProductCategory[] {
  const rows = extractCategoryList(payload)
    .map(toCategoryRow)
    .filter((row): row is CategoryRow => row !== null);

  const categoriesByName = new Map<string, ProductCategory>();
  for (const row of rows) {
    categoriesByName.set(row.key, {
      id: row.id,
      name: row.name,
      category_type: row.category_type,
    });
  }

  return Array.from(categoriesByName.values());
}
